<template>
  <!--
    One drop target: the Check-in column, the Check-out column, or a room tile. The WHOLE element is the vuedraggable
    container (the title block rides in its `header` slot), so a card dropped anywhere on the tile lands here.

    CONTROLLED: `:value` with no `@input`. vuedraggable restores the DOM after every drag and never touches the list;
    the page decides what a drop means (a move, a refusal, or nothing) and rebuilds the board from the document.
    A reorder inside one list fires `moved`, not `added`, so it never reaches the page.
  -->
  <draggable
    tag="div"
    class="ops-col"
    :class="classes"
    :value="cards"
    :data-col="String(colId)"
    :data-test="`ops-col-${colId}`"
    v-bind="dragOptions"
    @start="$emit('drag-start', $event)"
    @end="$emit('drag-end', $event)"
    @change="onChange"
  >
    <template #header>
      <div v-if="hasHead" class="ops-col__head" :class="{ 'ops-col__head--tile': isTile }">
        <div class="ops-col__titlerow">
          <span class="ops-col__title" data-test="ops-col-title">{{ title }}</span>
          <span v-if="count !== '' && count !== null" class="ops-col__count" data-test="ops-col-count">{{ count }}</span>
        </div>
        <div v-if="sub" class="ops-col__sub" data-test="ops-col-sub">{{ sub }}</div>
        <div v-if="warnA" class="ops-col__warn" data-test="ops-col-warn">{{ warnA }}<span class="ops-col__warn-b">{{ warnB }}</span></div>
      </div>
    </template>

    <div v-for="card in cards" :key="card.key" class="ops-col__item" data-test="ops-col-item">
      <slot name="card" :card="card"></slot>
    </div>

    <template #footer>
      <div v-if="skeleton" class="ops-col__skel" aria-hidden="true"><span></span><span></span></div>
      <!-- An empty list may draw its own empty state (the Operatory view's "Room open" card). It sits INSIDE the drop container. -->
      <slot v-else-if="cards.length === 0 && $scopedSlots.empty" name="empty"></slot>
      <div v-else-if="hint" class="ops-col__hint" :class="{ 'ops-col__hint--busy': busy, 'ops-col__hint--drop': hover && !busy }" data-test="ops-col-hint">{{ hint }}</div>
    </template>
  </draggable>
</template>

<script>
/**
 * A Practice Ops drop target (plan "Design review" → "Rooms wrap into a grid", whole card = handle, whole column / room
 * tile = target). Used three ways: the sticky Check-in column (left), the Check-out column (top right) and, as
 * `variant="tile"`, one room in the wrapping grid.
 *
 *   emits  add({ to, card })   a card from ANOTHER list was dropped here (`to` is 'checkin' | 'checkout' | the OperatoryNum)
 *          drag-start / drag-end
 *
 * `variant="room"` and `variant="strip"` are the Operatory view's two targets: the pinned room's panel (no chrome of its own — the
 * view draws the border and the buttons around it) and the horizontal "Up next" queue. Both draw no header when there is no title.
 * An `empty` scoped slot replaces the dashed hint when the list is empty (still inside the drop container).
 *
 * The card's body is the `card` scoped slot (OpsBoard fills it with OpsPatientCard). `hover` / `busy` are the live
 * drop-target states the board tracks from the drag's move callback: hover = teal dashed + "Drop to seat in …";
 * busy = an occupied room under a drag, amber + "Room in use. …". Static hex: the board is a light surface.
 *
 * ⛔ No prop is named `loading` (mixin.js computeds shadow it), hence `skeleton`.
 */
import draggable from 'vuedraggable';

export default {
  name: 'OpsColumn',

  components: { draggable },

  props: {
    /** 'checkin' | 'checkout' | an OperatoryNum. */
    colId: { type: [String, Number], required: true },
    variant: { type: String, default: 'column' },
    title: { type: String, default: '' },
    count: { type: [String, Number], default: '' },
    sub: { type: String, default: '' },
    warnA: { type: String, default: '' },
    warnB: { type: String, default: '' },
    /** board.js cards — the vuedraggable list. */
    cards: { type: Array, default: () => [] },
    /** True while a drag is over this target. */
    hover: { type: Boolean, default: false },
    /** True when that target is an OCCUPIED room (the drop will be refused). */
    busy: { type: Boolean, default: false },
    /** "Open", "No one waiting" … shown when the list is empty and nothing is being dragged over it. */
    emptyText: { type: String, default: '' },
    /** "Drop to seat in Room 3" … shown while hovered. */
    hoverText: { type: String, default: '' },
    sticky: { type: Boolean, default: false },
    skeleton: { type: Boolean, default: false },
    /** vuedraggable's move callback (shared by every list so the board can track the target under the pointer). */
    move: { type: Function, default: null },
    /** False turns dragging off (a read-only surface). */
    interactive: { type: Boolean, default: true },
  },

  computed: {
    isTile() {
      return this.variant === 'tile';
    },

    hasHead() {
      return !!(this.title || this.sub || this.warnA || (this.count !== '' && this.count !== null));
    },

    classes() {
      return [
        `ops-col--${this.variant}`,
        {
          'ops-col--sticky': this.sticky,
          'ops-col--hover': this.hover && !this.busy,
          'ops-col--busy': this.busy,
        },
      ];
    },

    hint() {
      if (this.busy) return 'Room in use. Check out the current patient first.';
      if (this.hover) return this.hoverText || this.emptyText;
      return this.cards.length === 0 ? this.emptyText : '';
    },

    dragOptions() {
      const options = {
        group: { name: 'practice-ops', pull: true, put: true },
        draggable: '.ops-col__item',
        animation: 150,
        disabled: !this.interactive,
        ghostClass: 'ops-ghost',
        chosenClass: 'ops-chosen',
        dragClass: 'ops-dragging',
        // A tap on a button inside a card (bell, ⋯, an alert icon) is a click, never a drag.
        filter: 'button, [data-no-drag]',
        preventOnFilter: false,
        // On touch, a short press starts a drag; a swipe still scrolls the column.
        delay: 120,
        delayOnTouchOnly: true,
        touchStartThreshold: 4,
        swapThreshold: 0.65,
        emptyInsertThreshold: 50,
      };
      if (this.move) options.move = this.move;
      return options;
    },
  },

  methods: {
    onChange(evt) {
      if (evt && evt.added) this.$emit('add', { to: String(this.colId), card: evt.added.element });
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-col {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  background: #eff3f2;
  border: 1.5px solid #e3e8e7;
}

.ops-col--column {
  border-radius: 14px;
  overflow-y: auto;
  padding: 0 7px 8px;
  gap: 8px;
}

.ops-col--tile {
  border-radius: 12px;
  padding: 8px;
  gap: 6px;
}

// The Operatory view's pinned room: the view draws the panel (border, tint, buttons); the list itself is transparent.
.ops-col--room {
  flex: 1;
  background: transparent;
  border: 0;
  padding: 0;
  gap: 10px;
}

.ops-col--room.ops-col--hover,
.ops-col--room.ops-col--busy {
  background: transparent;
  border: 0;
}

// The "Up next" queue: one row of cards that scrolls sideways (the page itself never does).
.ops-col--strip {
  flex-direction: row;
  align-items: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 0 4px;
  gap: 10px;
  background: transparent;
  border: 0;

  .ops-col__item { width: 236px; }
  .ops-col__hint { flex: 1; min-width: 200px; }
}

.ops-col--strip.ops-col--hover {
  background: #e9f6f1;
  border: 1.5px dashed #0f7259;
  border-radius: 14px;
}

.ops-col--sticky {
  position: sticky;
  left: 0;
  z-index: 3;
  box-shadow: 8px 0 14px -10px rgba(12, 15, 14, 0.18);
}

// The drop target under the pointer: a free room / column is teal and dashed; an occupied room is amber.
.ops-col--hover {
  background: #e9f6f1;
  border: 1.5px dashed #0f7259;
}

.ops-col--busy {
  background: #fef6e7;
  border: 1.5px solid #f59e0b;
}

.ops-col__head {
  padding: 11px 5px 1px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ops-col__head--tile {
  padding: 0 2px;
  flex-direction: row;
  align-items: baseline;
  gap: 6px;

  .ops-col__sub {
    flex: 1;
    text-align: right;
    font-size: 11.5px;
  }
}

.ops-col__titlerow {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ops-col__head--tile .ops-col__titlerow { flex: none; }

.ops-col__title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ops-col--tile .ops-col__title {
  flex: none;
  font-size: 13px;
}

.ops-col__count {
  font-size: 12px;
  font-weight: 700;
  color: #707876;
}

.ops-col__sub {
  min-width: 0;
  font-size: 12px;
  color: #707876;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ops-col__warn {
  font-size: 12px;
  font-weight: 600;
  color: #3a4240;
  white-space: nowrap;
}

.ops-col__warn-b {
  color: #b45309;
  font-weight: 700;
}

.ops-col__item {
  flex: none;
  cursor: grab;
}

.ops-col__hint {
  flex: none;
  border: 1.5px dashed #d5dcda;
  border-radius: 12px;
  padding: 16px 12px;
  font-size: 12.5px;
  line-height: 1.45;
  color: #707876;
  text-align: center;
  font-weight: 600;
}

.ops-col--tile .ops-col__hint {
  flex: 1;
  min-height: 40px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  padding: 6px;
  font-size: 12px;
}

.ops-col__hint--drop {
  border-color: #0f7259;
  color: #0b5c49;
}

.ops-col__hint--busy {
  border-color: #f59e0b;
  color: #b45309;
}

.ops-col__skel {
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    display: block;
    height: 100px;
    border-radius: 12px;
    background: #e3e8e7;
  }
}

// Sortable's own classes (set through dragOptions) — scoped styles reach them because the cards are this file's slot content.
.ops-ghost { opacity: 0.45; }
.ops-chosen { opacity: 0.95; }
.ops-dragging { box-shadow: 0 12px 28px rgba(12, 15, 14, 0.2); }
</style>
